import React, { useState } from 'react';
import { X, Play, Zap, AlertCircle, Settings, Calendar } from 'lucide-react';
import { triggerSapJobAPI } from '../../../api/sapSync';

interface TriggerJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
}

const JOB_TYPE_PRESETS = [
  { value: 'INBOUND_INVOICES', label: 'Inbound Invoices (SAP -> ERP)', desc: 'Fetch & process invoice text/xml from SFTP inbound' },
  { value: 'OUTBOUND_ORDERS', label: 'Outbound Orders (ERP -> SAP)', desc: 'Generate & upload sales orders to SFTP outbound' },
  { value: 'INBOUND_CUSTOMERS', label: 'Inbound Customers / Master Data', desc: 'Sync customer & distributor master records' },
  { value: 'INBOUND_PAYMENTS', label: 'Inbound Payments / Ledgers', desc: 'Sync distributor ledger & payment collections' },
  { value: 'OUTBOUND_PRICES', label: 'Outbound Pricing / Price Changes', desc: 'Push price lists to SAP pricing schema' },
  { value: 'INBOUND_INVENTORY', label: 'Inbound Inventory / Stock Levels', desc: 'Sync warehouse plant & depo stock balances' },
  { value: 'CUSTOM', label: 'Custom Job Identifier', desc: 'Manually specify custom jobType string' },
];

export const TriggerJobModal: React.FC<TriggerJobModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('INBOUND_INVOICES');
  const [customJobType, setCustomJobType] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [rawPayload, setRawPayload] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const actualJobType = selectedPreset === 'CUSTOM' ? customJobType.trim() : selectedPreset;
    if (!actualJobType) {
      setError('Please provide a valid Job Type identifier.');
      return;
    }

    let parsedExtra: Record<string, any> = {};
    if (targetDate) {
      parsedExtra.date = targetDate;
    }
    if (rawPayload.trim()) {
      try {
        parsedExtra = { ...parsedExtra, ...JSON.parse(rawPayload.trim()) };
      } catch {
        setError('Invalid JSON in optional parameters field.');
        return;
      }
    }

    setLoading(true);
    try {
      const res = await triggerSapJobAPI(actualJobType, parsedExtra);
      onSuccess(res.message || `Job ${actualJobType} successfully triggered!`);
      onClose();
    } catch (err: any) {
      console.error('Trigger job failed:', err);
      setError(err.message || 'Failed to trigger SAP job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-scaleUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">Trigger SAP Job</h3>
              <p className="text-xs text-slate-500">Manually queue or dispatch a sync job type</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto space-y-4 flex-1">
            {error && (
              <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 flex items-start gap-2.5 text-xs text-rose-700">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Job Type Selection */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                Select Job Type <span className="text-rose-500">*</span>
              </label>
              <div className="space-y-2">
                {JOB_TYPE_PRESETS.map((preset) => (
                  <label
                    key={preset.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      selectedPreset === preset.value
                        ? 'bg-purple-50/60 border-purple-400 ring-1 ring-purple-400'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="jobTypePreset"
                      value={preset.value}
                      checked={selectedPreset === preset.value}
                      onChange={(e) => setSelectedPreset(e.target.value)}
                      className="mt-0.5 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-bold text-slate-800 font-mono">{preset.value}</div>
                      <p className="text-[11px] text-slate-500 mt-0.5">{preset.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Job Type Input */}
            {selectedPreset === 'CUSTOM' && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Custom Job Type Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. INBOUND_STATEMENT_RECON"
                  value={customJobType}
                  onChange={(e) => setCustomJobType(e.target.value)}
                  className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 font-mono"
                  required
                />
              </div>
            )}

            {/* Optional Target Date */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                Target Date (Optional)
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
              />
            </div>

            {/* Optional JSON Parameters */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5 text-slate-400" />
                Extra Parameters (Optional JSON)
              </label>
              <textarea
                rows={3}
                placeholder='{ "force": true, "batchLimit": 100 }'
                value={rawPayload}
                onChange={(e) => setRawPayload(e.target.value)}
                className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 font-mono"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Triggering...
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 fill-current" />
                  Dispatch Job Now
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TriggerJobModal;
